import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Badge,
  Box,
  Button as ChakraButton,
  Flex,
  Icon,
  Input,
  Select,
  SimpleGrid,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FiAlertCircle,
  FiCheckCircle,
  FiClock,
  FiRefreshCcw,
  FiTarget,
  FiUser,
} from 'react-icons/fi';
import { IoSearchSharp } from 'react-icons/io5';
import { useNavigate } from 'react-router-dom';
import Button from '../components/Form/Button';
import HeaderSideBar from '../components/HeaderSideBar';
import TaskListStatusIcon from '../components/TaskListStatusIcon';
import TaskDetailDrawer from '../components/task/TaskDetailDrawer';
import { PermissionByIdDTO, TaskPropsDTO } from '../dtos';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { getFormatDate } from '../utils/date';

type StatusFilter = 'all' | 'BACKLOG' | 'FAZENDO' | 'CONCLUIDO';
type PriorityFilter = 'all' | 'ALTA' | 'MEDIA' | 'BAIXA';

type ResponsibleOption = {
  label: string;
  value: string;
};

function getPriorityStyles(priority?: string) {
  switch (priority) {
    case 'ALTA':
      return { bg: '#FEE2E2', color: '#B91C1C', label: 'Alta' };
    case 'MEDIA':
      return { bg: '#FEF3C7', color: '#B45309', label: 'Media' };
    default:
      return { bg: '#E2E8F0', color: '#475569', label: 'Baixa' };
  }
}

function getStatusStyles(status?: string) {
  switch (status) {
    case 'FAZENDO':
      return { bg: '#DBEAFE', color: '#1D4ED8', label: 'Em andamento' };
    case 'CONCLUIDO':
      return { bg: '#DCFCE7', color: '#166534', label: 'Concluida' };
    default:
      return { bg: '#FEF3C7', color: '#92400E', label: 'Pendente' };
  }
}

export default function Tarefa() {
  const navigate = useNavigate();
  const toast = useToast();
  const cancelRef = useRef() as React.MutableRefObject<HTMLButtonElement>;
  const { office, role } = useAuth();
  const [taskList, setTaskList] = useState<TaskPropsDTO[]>([]);
  const [selectedTask, setSelectedTask] = useState<TaskPropsDTO | null>(null);
  const [responsibles, setResponsibles] = useState<ResponsibleOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [priorityFilter, setPriorityFilter] = useState<PriorityFilter>('all');
  const {
    isOpen: isDrawerOpen,
    onOpen: onDrawerOpen,
    onClose: onDrawerClose,
  } = useDisclosure();
  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure();
  const [taskToDelete, setTaskToDelete] = useState<string | null>(null);

  const canEdit = role?.tarefas_page > 1 || role?.demandas_page > 1;

  const fetchTasks = useCallback(async () => {
    setLoading(true);

    try {
      const response = await api.get(`/task/office/${office.id}/responsible`);
      const tasks = response.data || [];

      tasks.sort((a: TaskPropsDTO, b: TaskPropsDTO) => {
        const statusOrder = {
          BACKLOG: 0,
          FAZENDO: 1,
          CONCLUIDO: 2,
        };

        const priorityOrder = {
          ALTA: 0,
          MEDIA: 1,
          BAIXA: 2,
        };

        const statusDiff =
          statusOrder[a.status as keyof typeof statusOrder] -
          statusOrder[b.status as keyof typeof statusOrder];

        if (statusDiff !== 0) return statusDiff;

        const priorityDiff =
          priorityOrder[a.priority as keyof typeof priorityOrder] -
          priorityOrder[b.priority as keyof typeof priorityOrder];

        if (priorityDiff !== 0) return priorityDiff;

        return new Date(b.date).getTime() - new Date(a.date).getTime();
      });

      setTaskList(tasks);
    } catch (error) {
      console.error('Failed to load tasks', error);
      setTaskList([]);
    } finally {
      setLoading(false);
    }
  }, [office.id]);

  const fetchResponsibles = useCallback(async () => {
    try {
      const response = await api.get(`/permission/office/${role?.office_id}/responsible`);

      setResponsibles(
        response.data.map((item: PermissionByIdDTO) => ({
          value: item.user.id as string,
          label: item.user.name,
        })),
      );
    } catch (error) {
      setResponsibles(
        selectedTask?.responsible_id && selectedTask?.responsible?.name
          ? [
              {
                value: selectedTask.responsible_id,
                label: selectedTask.responsible.name,
              },
            ]
          : [],
      );
    }
  }, [role?.office_id, selectedTask?.responsible?.name, selectedTask?.responsible_id]);

  const fetchTaskDetails = useCallback(
    async (taskId: string) => {
      setDrawerLoading(true);
      onDrawerOpen();

      try {
        const response = await api.get(`/task/${taskId}`);
        setSelectedTask(response.data);
      } catch (error) {
        toast({
          title: 'Nao foi possivel carregar a tarefa.',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
        onDrawerClose();
      } finally {
        setDrawerLoading(false);
      }
    },
    [onDrawerClose, onDrawerOpen, toast],
  );

  useEffect(() => {
    if (office.id) {
      fetchTasks();
    }
  }, [fetchTasks, office.id]);

  useEffect(() => {
    if (role?.office_id) {
      fetchResponsibles();
    }
  }, [fetchResponsibles, role?.office_id]);

  const filteredTasks = useMemo(() => {
    return taskList.filter(task => {
      const normalizedQuery = query.trim().toLowerCase();
      const matchesQuery =
        !normalizedQuery ||
        String(task.id).toLowerCase().includes(normalizedQuery) ||
        task.title?.toLowerCase().includes(normalizedQuery) ||
        task.voter?.name?.toLowerCase().includes(normalizedQuery);

      const matchesStatus = statusFilter === 'all' || task.status === statusFilter;
      const matchesPriority =
        priorityFilter === 'all' || task.priority === priorityFilter;

      return matchesQuery && matchesStatus && matchesPriority;
    });
  }, [priorityFilter, query, statusFilter, taskList]);

  const summary = useMemo(() => {
    const total = taskList.length;
    const backlog = taskList.filter(task => task.status === 'BACKLOG').length;
    const doing = taskList.filter(task => task.status === 'FAZENDO').length;
    const completed = taskList.filter(task => task.status === 'CONCLUIDO').length;
    const highPriority = taskList.filter(task => task.priority === 'ALTA').length;

    return { total, backlog, doing, completed, highPriority };
  }, [taskList]);

  const handleQuickUpdate = useCallback(
    async (
      taskId: string,
      payload: {
        status?: string;
        priority?: string;
        responsible_id?: string;
      },
    ) => {
      setActionLoading(true);

      try {
        await api.patch(`/task/${taskId}/quick`, payload);
        toast({
          title: 'Tarefa atualizada',
          status: 'success',
          duration: 2500,
          isClosable: true,
          position: 'top-right',
        });
        await fetchTasks();
        if (selectedTask?.id === taskId) {
          await fetchTaskDetails(taskId);
        }
      } catch (error: any) {
        toast({
          title:
            error?.response?.data?.message ||
            'Nao foi possivel atualizar a tarefa.',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      } finally {
        setActionLoading(false);
      }
    },
    [fetchTaskDetails, fetchTasks, selectedTask?.id, toast],
  );

  const handleStatusChange = async (status: string, id: string) => {
    await handleQuickUpdate(id, { status });
  };

  const handleDeleteDialog = (taskId: string) => {
    setTaskToDelete(taskId);
    onDeleteOpen();
  };

  const handleDelete = async () => {
    if (!taskToDelete) return;

    setActionLoading(true);

    try {
      await api.delete(`/task/${taskToDelete}`);
      toast({
        title: 'Tarefa excluida com sucesso',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });

      if (selectedTask?.id === taskToDelete) {
        setSelectedTask(null);
        onDrawerClose();
      }

      await fetchTasks();
    } catch (error: any) {
      toast({
        title:
          error?.response?.data?.message ||
          'Ocorreu um erro ao excluir a tarefa.',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
    } finally {
      setActionLoading(false);
      setTaskToDelete(null);
      onDeleteClose();
    }
  };

  return (
    <HeaderSideBar>
      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteClose}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent mx={4}>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Excluir tarefa
            </AlertDialogHeader>
            <AlertDialogBody>
              Essa acao e irreversivel. Deseja remover a tarefa selecionada?
            </AlertDialogBody>
            <AlertDialogFooter>
              <ChakraButton onClick={onDeleteClose} ref={cancelRef}>
                Cancelar
              </ChakraButton>
              <Button
                width="fit-content"
                bgColor="#DC2626"
                color="white"
                isLoading={actionLoading}
                onClick={handleDelete}
                ml={3}
              >
                Excluir
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      <TaskDetailDrawer
        isOpen={isDrawerOpen}
        onClose={onDrawerClose}
        task={selectedTask}
        loading={drawerLoading}
        canEdit={canEdit}
        responsibles={responsibles}
        actionLoading={actionLoading}
        onEdit={taskId => navigate(`/demanda/${taskId}`)}
        onDelete={handleDeleteDialog}
        onQuickUpdate={handleQuickUpdate}
      />

      <Flex direction="column" gap="16px" pb="24px">
        <Box
          borderRadius="24px"
          px={{ base: '20px', md: '28px' }}
          py={{ base: '22px', md: '28px' }}
          bgGradient={`linear(135deg, ${office?.primary_color || '#0F766E'} 0%, ${
            office?.secondary_color || '#0F172A'
          } 100%)`}
          color="white"
          boxShadow="0 24px 80px rgba(15, 23, 42, 0.18)"
        >
          <Flex justify="space-between" gap="16px" direction={{ base: 'column', lg: 'row' }}>
            <Box maxW="720px">
              <Badge
                bg="rgba(255,255,255,0.14)"
                color="white"
                borderRadius="full"
                px="12px"
                py="4px"
                mb="12px"
              >
                Fila de execucao
              </Badge>
              <Text fontSize={{ base: '24px', md: '32px' }} fontWeight="700">
                Tarefas sob sua atuacao
                {loading && <Spinner color="white" ml="3" size="sm" />}
              </Text>
              <Text mt="8px" color="rgba(255,255,255,0.82)" maxW="620px">
                Priorize o que esta parado, acompanhe o que esta em andamento e conclua
                rapido o que depende de voce.
              </Text>
            </Box>

            <Button
              w={{ base: '100%', lg: '220px' }}
              bgColor="rgba(255,255,255,0.16)"
              color="white"
              leftIcon={<FiRefreshCcw />}
              onClick={fetchTasks}
              isLoading={loading}
            >
              Atualizar fila
            </Button>
          </Flex>
        </Box>

        <SimpleGrid columns={{ base: 1, md: 2, xl: 5 }} spacing="16px">
          {[
            { label: 'Total na fila', value: summary.total, icon: FiTarget },
            { label: 'Pendentes', value: summary.backlog, icon: FiClock },
            { label: 'Em andamento', value: summary.doing, icon: FiUser },
            { label: 'Concluidas', value: summary.completed, icon: FiCheckCircle },
            { label: 'Alta prioridade', value: summary.highPriority, icon: FiAlertCircle },
          ].map(item => (
            <Box
              key={item.label}
              bg="white"
              borderRadius="20px"
              p="18px"
              border="1px solid #E2E8F0"
              boxShadow="0 20px 60px rgba(15, 23, 42, 0.08)"
            >
              <Flex justify="space-between" align="center" mb="14px">
                <Text fontSize="13px" color="#64748B" fontWeight="600">
                  {item.label}
                </Text>
                <Icon as={item.icon} fontSize="18px" color="#0F766E" />
              </Flex>
              <Text fontSize="34px" lineHeight="1" fontWeight="700" color="#0F172A">
                {item.value}
              </Text>
            </Box>
          ))}
        </SimpleGrid>

        <Box
          bg="white"
          borderRadius="24px"
          p={{ base: '18px', md: '22px' }}
          border="1px solid #E2E8F0"
          boxShadow="0 20px 60px rgba(15, 23, 42, 0.08)"
        >
          <Flex
            justify="space-between"
            gap="12px"
            direction={{ base: 'column', xl: 'row' }}
            align={{ base: 'stretch', xl: 'center' }}
          >
            <Box>
              <Text fontSize="20px" fontWeight="700" color="#0F172A">
                Recorte operacional
              </Text>
              <Text fontSize="13px" color="#64748B">
                Encontre rapido por titulo, id ou apoiador e foque por status e prioridade.
              </Text>
            </Box>

            <Flex gap="10px" direction={{ base: 'column', md: 'row' }}>
              <Box position="relative">
                <Input
                  value={query}
                  onChange={event => setQuery(event.target.value)}
                  placeholder="Buscar por titulo, id ou apoiador"
                  pl="38px"
                  w={{ base: '100%', md: '300px' }}
                  borderColor="#CBD5E1"
                  bg="#F8FAFC"
                />
                <Icon
                  as={IoSearchSharp}
                  position="absolute"
                  left="12px"
                  top="50%"
                  transform="translateY(-50%)"
                  color="#64748B"
                />
              </Box>

              <Select
                value={statusFilter}
                onChange={event => setStatusFilter(event.target.value as StatusFilter)}
                w={{ base: '100%', md: '190px' }}
                borderColor="#CBD5E1"
                bg="#F8FAFC"
              >
                <option value="all">Todos os status</option>
                <option value="BACKLOG">Pendentes</option>
                <option value="FAZENDO">Em andamento</option>
                <option value="CONCLUIDO">Concluidas</option>
              </Select>

              <Select
                value={priorityFilter}
                onChange={event => setPriorityFilter(event.target.value as PriorityFilter)}
                w={{ base: '100%', md: '190px' }}
                borderColor="#CBD5E1"
                bg="#F8FAFC"
              >
                <option value="all">Todas as prioridades</option>
                <option value="ALTA">Alta</option>
                <option value="MEDIA">Media</option>
                <option value="BAIXA">Baixa</option>
              </Select>
            </Flex>
          </Flex>
        </Box>

        <Box
          bg="white"
          borderRadius="24px"
          border="1px solid #E2E8F0"
          boxShadow="0 20px 60px rgba(15, 23, 42, 0.08)"
          overflow="hidden"
        >
          <Box overflow="auto">
            <Table>
              <Thead bg="#F8FAFC">
                <Tr>
                  <Th color="#475569">Id</Th>
                  <Th color="#475569">Status</Th>
                  <Th color="#475569">Titulo</Th>
                  <Th color="#475569">Prioridade</Th>
                  <Th color="#475569">Apoiador</Th>
                  <Th color="#475569">Prazo</Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredTasks.length > 0 ? (
                  filteredTasks.map(task => {
                    const priorityStyles = getPriorityStyles(task.priority);
                    const statusStyles = getStatusStyles(task.status);

                    return (
                      <Tr key={task.id} _hover={{ bg: '#F8FAFC' }}>
                        <Td
                          borderColor="#E2E8F0"
                          cursor="pointer"
                          onClick={() => fetchTaskDetails(task.id)}
                        >
                          <Text fontWeight="700" color="#0F172A">
                            #{task.id}
                          </Text>
                        </Td>

                        <Td borderColor="#E2E8F0">
                          <Flex align="center" gap="10px">
                            <TaskListStatusIcon
                              buttonId={task.id}
                              statusChange={handleStatusChange}
                              status={task.status}
                            />
                            <Badge
                              borderRadius="full"
                              px="10px"
                              py="4px"
                              bg={statusStyles.bg}
                              color={statusStyles.color}
                            >
                              {statusStyles.label}
                            </Badge>
                          </Flex>
                        </Td>

                        <Td
                          borderColor="#E2E8F0"
                          cursor="pointer"
                          onClick={() => fetchTaskDetails(task.id)}
                        >
                          <Text fontWeight="700" color="#0F172A">
                            {task.title}
                          </Text>
                          <Text fontSize="12px" color="#64748B">
                            Criada em {getFormatDate(new Date(task.date), 'dd/MM/yyyy')}
                          </Text>
                        </Td>

                        <Td borderColor="#E2E8F0">
                          <Badge
                            borderRadius="full"
                            px="10px"
                            py="4px"
                            bg={priorityStyles.bg}
                            color={priorityStyles.color}
                          >
                            {priorityStyles.label}
                          </Badge>
                        </Td>

                        <Td
                          borderColor="#E2E8F0"
                          cursor="pointer"
                          onClick={() => fetchTaskDetails(task.id)}
                        >
                          <Text color="#334155">{task.voter?.name || 'Nao informado'}</Text>
                        </Td>

                        <Td borderColor="#E2E8F0">
                          <Text color="#334155">
                            {task.deadline
                              ? getFormatDate(new Date(task.deadline), 'dd/MM/yyyy')
                              : '-'}
                          </Text>
                        </Td>
                      </Tr>
                    );
                  })
                ) : (
                  <Tr>
                    <Td colSpan={6} py="24px" textAlign="center" color="#64748B">
                      Nenhuma tarefa encontrada para os filtros atuais.
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </Box>
        </Box>
      </Flex>
    </HeaderSideBar>
  );
}
